import { SetterOrUpdater } from "recoil";
import {
  MissingRequisitionResponse,
  UserDefaultRequisitionsResponse,
} from "../../../recoil/types";
import { getBaseUrl } from "../../../utils/requestUtils";
import { NewRequisitionType } from "../../NewRequisition/types";
import { fetchOrLogout } from "../../../utils/helpers";
import { AddOnMode } from "../types";

export const GetRequisitions = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response[]> => {
  const baseUrl = await getBaseUrl();
  return Promise.all([
    fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/requisitions/?requisition_type=completed`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    ),
    fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/requisitions/?requisition_type=processing`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    ),
  ]);
};

export const GetSearchRequisitions = async (
  token: string,
  searchText: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/requisitions/?search=${searchText}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const GetSpecificRequisition = async (
  token: string,
  sfid: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/requisitions/${sfid}/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const RefreshRequisitionRows = async (
  token: string,
  requisitionSetter: SetterOrUpdater<
    UserDefaultRequisitionsResponse | undefined
  >,
  setLoggedOut: (inactivity: boolean) => void,
  searchText?: string,
  requisitionCompleteSetter?: SetterOrUpdater<boolean>,
): Promise<void> => {
  try {
    if (searchText) {
      const requisitionsResponse = await GetSearchRequisitions(
        token,
        searchText,
        setLoggedOut,
      );
      if (requisitionsResponse.ok) {
        const requisitions =
          (await requisitionsResponse.json()) as UserDefaultRequisitionsResponse;
        requisitionSetter({
          ...requisitions,
          total_completed: requisitions.requisitions_completed.length,
          total_processing: requisitions.requisitions_processing.length,
        });
      }
    } else {
      const requisitionsResponse = await GetRequisitions(token, setLoggedOut);
      const requisitions = (await Promise.all(
        requisitionsResponse.map(async (response) => response.json()),
      )) as UserDefaultRequisitionsResponse[];
      const totalRequisitions = {
        requisitions_completed: [...requisitions[0].requisitions_completed],
        requisitions_processing: [...requisitions[1].requisitions_processing],
        total_count:
          requisitions[0].total_count != null &&
          requisitions[1].total_count != null
            ? requisitions[0].total_count + requisitions[1].total_count
            : null,
        total_completed: requisitions[0].total_count ?? 0,
        total_processing: requisitions[1].total_count ?? 0,
      } as UserDefaultRequisitionsResponse;
      await requisitionSetter(totalRequisitions);
      if (requisitionCompleteSetter) {
        requisitionCompleteSetter(true);
      }
    }
  } catch (e) {
    return undefined;
  }
};

export const GetMissingInfo = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/missing_info`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const RefreshMissingInfo = async (
  token: string,
  missingRequisitionSetter: SetterOrUpdater<MissingRequisitionResponse[]>,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<void> => {
  try {
    const requisitionsResponse = await GetMissingInfo(token, setLoggedOut);
    if (requisitionsResponse.ok) {
      const requisitions =
        (await requisitionsResponse.json()) as MissingRequisitionResponse[];
      missingRequisitionSetter(requisitions);
    } else {
      missingRequisitionSetter([]);
    }
  } catch (e) {
    return undefined;
  }
};

export const ReleasePatientReport = async (
  token: string,
  carrier: boolean,
  reportID: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/${
      carrier ? "unityreports" : "aneuploidyreports"
    }/${reportID}/release_to_patient/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const GetAllNewRequisitionOrders = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/electronic_orders/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const RefreshNewRequisitionOrders = async (
  token: string,
  requisitionSetter: SetterOrUpdater<NewRequisitionType[]>,
  setLoggedOut: (inactivity: boolean) => void,
  requisitionCompleteSetter?: SetterOrUpdater<boolean>,
): Promise<void> => {
  try {
    const ordersResponse = await GetAllNewRequisitionOrders(
      token,
      setLoggedOut,
    );
    if (ordersResponse.ok) {
      const allOrders = (await ordersResponse.json()) as NewRequisitionType[];
      requisitionSetter(allOrders);
      if (requisitionCompleteSetter) {
        requisitionCompleteSetter(true);
      }
    }
  } catch (e) {
    return undefined;
  }
};

export const ResetAllTreatmentNotes = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/treatment_notes/amend_treatment_notes`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const SubmitAddOns = async (
  token: string,
  req_identifier: string,
  add_on_types: AddOnMode[],
  setLoggedOut: (inactivity: boolean) => void,
  fetalAntigenPanel?: string[],
  fetalAntigenFileUrl?: string,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  const body: Record<string, unknown> = {
    req_identifier,
    add_on_types,
  };
  if (fetalAntigenPanel !== undefined) {
    body.fetal_antigen_panel = fetalAntigenPanel;
  }
  if (fetalAntigenFileUrl !== undefined) {
    body.fetal_antigen_file = fetalAntigenFileUrl;
  }
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/add_on`,
    {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const ResetDemoReqs = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  // used for provider demo purposes to reset the cache
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/reset_reqs`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const ResetOnlineOrders = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  // used for provider demo purposes to delete test online orders
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/reset_orders`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const GetArchivedRequisitions = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/requisitions/?requisition_type=archived`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const UpdateArchiveStatus = async (
  token: string,
  sfid: string,
  status: "archived" | "completed",
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/portal_requisition/${sfid}/`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ provider_portal_status: status }),
    },
    setLoggedOut,
  );
};
